/**
 * 图片管理分组列表视图。
 *
 * 仅负责把已规范化的分组数据创建为 DOM；请求、选择状态、事件委托和写操作
 * 仍由 image-manager 控制器持有，避免渲染层触及业务状态或传输层。
 */

const IMAGE_ONLY_TITLE_MARKER = '__image_only__';

export function getImageManagerGroupKey(group) {
  if (group.listing_id != null) return `listing:${group.listing_id}`;
  return `job:${group.job_id || 'orphan'}`;
}

function createElement(tagName, { className = '', text = '', attrs = {}, dataset = {} } = {}) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text) element.textContent = text;
  for (const [name, value] of Object.entries(attrs)) {
    element.setAttribute(name, String(value));
  }
  for (const [name, value] of Object.entries(dataset)) {
    element.dataset[name] = String(value);
  }
  return element;
}

export function parseImageManagerMetadata(metadata) {
  if (!metadata) return {};
  if (typeof metadata === 'string') {
    try { return JSON.parse(metadata); } catch { return {}; }
  }
  return metadata;
}

export function formatImageManagerOwnerLabel(source) {
  if (!source) return '';
  if (source.owner_user_name === '多人') return '创建人：多人';
  const email = source.owner_user_email;
  if (source.owner_user_name && email) {
    return `创建人：${source.owner_user_name} · ${email}`;
  }
  if (source.owner_user_name) return `创建人：${source.owner_user_name}`;
  if (source.userDisplayName) return `创建人：${source.userDisplayName}`;
  if (source.owner_user_id) return `创建人：用户#${source.owner_user_id}`;
  if (source.user_id) return `创建人：用户#${source.user_id}`;
  return '';
}

function createOwnerBadge(source) {
  const label = formatImageManagerOwnerLabel(source);
  if (!label) return null;
  const text = label.replace(/^创建人：/, '');
  return createElement('span', {
    className: 'im-owner-badge',
    text: `👤 ${text}`,
    attrs: { title: label },
  });
}

function createListingHeader(group, listingIdx, { listingName, isExpanded, lastDate, countLabel }) {
  const header = createElement('div', {
    className: 'im-listing-header',
    dataset: { listingIdx },
  });
  const chevron = createElement('span', { className: 'im-chevron' });
  if (isExpanded) chevron.classList.add('is-expanded');
  const chevronIcon = createElement('svg', {
    attrs: {
      width: '14', height: '14', viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor',
      'stroke-width': '2.5', 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'aria-hidden': 'true',
    },
  });
  chevronIcon.append(createElement('polyline', { attrs: { points: '9 18 15 12 9 6' } }));
  chevron.append(chevronIcon);

  const info = createElement('div', { className: 'im-listing-info' });
  const titleRow = createElement('div', { className: 'im-listing-title-row' });
  titleRow.append(createElement('div', { className: 'im-listing-name', text: listingName }));
  const ownerBadge = createOwnerBadge(group);
  if (ownerBadge) titleRow.append(ownerBadge);
  if ((group.workflow === 'image_only' || group.is_standalone) && group.listing_id) {
    titleRow.append(createElement('button', {
      className: 'btn btn-secondary btn-sm im-rename-btn',
      text: '重命名',
      attrs: { type: 'button' },
      dataset: { listingId: group.listing_id, listingName: group.listing_name || '' },
    }));
  }
  info.append(titleRow);
  if (group.workflow === 'image_only' && group.listing_id) {
    info.append(createElement('a', {
      className: 'im-continue-assets',
      text: '继续生图',
      attrs: { href: `/image-suite.html?projectId=${encodeURIComponent(group.listing_id)}` },
    }));
  }
  if (group.listing_title && group.listing_title !== IMAGE_ONLY_TITLE_MARKER && group.workflow !== 'image_only') {
    info.append(createElement('div', { className: 'im-listing-subtitle', text: group.listing_title }));
  }
  header.append(chevron, info);
  if (lastDate) header.append(createElement('span', { className: 'im-listing-date', text: lastDate }));
  header.append(createElement('span', { className: 'im-listing-count', text: countLabel }));
  return header;
}

function createImageStatus(status, errorMessage) {
  const content = createElement('div', { className: 'im-image-status-content' });
  if (status === 'processing' || status === 'pending') {
    content.append(createElement('span', { className: 'im-image-status-spinner', attrs: { 'aria-hidden': 'true' } }));
  }
  const text = status === 'pending' ? '等待生成...'
    : status === 'processing' ? '生成中...'
      : status === 'failed' ? errorMessage || '生成失败'
        : '无图片';
  content.append(createElement('span', { className: 'im-image-status-text', text }));
  return content;
}

function createImageCard(img, listingIdx, imageIdx, { selectedIds, formatNumber }) {
  const meta = parseImageManagerMetadata(img.metadata);
  const slotLabel = meta.slotNumber ? `Slot ${meta.slotNumber}` : '图片';
  const slotType = meta.slotType || '';
  const variant = meta.variant || '';
  const provider = meta.provider || '';
  const model = meta.model || img.model || '';
  const costTokens = Number(img.cost_tokens || meta.costTokens || 0) || 0;
  const status = img.status || 'completed';
  const isSelected = selectedIds.has(img.id);
  const card = createElement('div', {
    className: 'image-card',
    dataset: { listingIdx, imageIdx, imageId: img.id },
  });
  card.classList.toggle('is-completed', status === 'completed');
  card.classList.toggle('is-pending', status !== 'completed');
  card.classList.toggle('is-selected', isSelected);

  const preview = createElement('div', { className: 'im-image-preview' });
  if (status === 'completed' && img.id) {
    preview.append(createElement('img', {
      className: 'im-image-thumbnail',
      attrs: { src: `/api/image/assets/${encodeURIComponent(img.id)}`, alt: slotLabel, loading: 'lazy' },
    }));
  } else {
    preview.append(createImageStatus(status, img.error_message));
  }
  if (status !== 'completed') {
    const statusLabel = status === 'pending' ? '排队中'
      : status === 'processing' ? '生成中'
        : status === 'failed' ? '失败' : status;
    const statusBadge = createElement('span', { className: 'im-image-status-badge', text: statusLabel });
    statusBadge.classList.add(`status-${['pending', 'processing', 'failed'].includes(status) ? status : 'other'}`);
    preview.append(statusBadge);
  }
  const selectControl = createElement('div', { className: 'im-image-select-control' });
  const checkbox = createElement('input', {
    className: 'im-image-checkbox',
    attrs: { type: 'checkbox' },
    dataset: { imageId: img.id },
  });
  checkbox.checked = isSelected;
  selectControl.append(checkbox);
  preview.append(selectControl);

  const metaBody = createElement('div', { className: 'im-image-meta' });
  metaBody.append(createElement('div', { className: 'im-image-title', text: slotLabel }));
  metaBody.append(createElement('div', { className: 'im-image-detail', text: variant ? `${slotType} · ${variant}` : slotType }));
  if (provider || model) {
    metaBody.append(createElement('div', {
      className: 'im-image-provider',
      text: `${provider || 'unknown'}${model ? ` · ${model}` : ''}`,
    }));
  }
  if (costTokens > 0) {
    metaBody.append(createElement('div', { className: 'im-image-cost', text: `LinkFox ${formatNumber(costTokens)} 算力` }));
  }
  const ownerBadge = createOwnerBadge(img);
  if (ownerBadge) {
    const owner = createElement('div', { className: 'im-image-owner' });
    owner.append(ownerBadge);
    metaBody.append(owner);
  }
  card.append(preview, metaBody);
  return card;
}

function createListingBody(images, listingIdx, { allSelected, groupSelected, selectedIds, formatNumber }) {
  const body = createElement('div', { className: 'im-listing-body' });
  const toolbar = createElement('div', { className: 'im-listing-toolbar' });
  const selectLabel = createElement('label', { className: 'im-select-all-label', text: '全选' });
  const selectAll = createElement('input', {
    className: 'im-select-all',
    attrs: { type: 'checkbox' },
    dataset: { listingIdx },
  });
  selectAll.checked = allSelected;
  selectLabel.prepend(selectAll);
  toolbar.append(selectLabel);
  if (groupSelected > 0) toolbar.append(createElement('span', { className: 'im-selected-count', text: `已选 ${groupSelected} 张` }));
  const grid = createElement('div', { className: 'im-image-grid' });
  grid.append(...images.map((img, imageIdx) => createImageCard(img, listingIdx, imageIdx, { selectedIds, formatNumber })));
  body.append(toolbar, grid);
  return body;
}

/**
 * Render a complete grouping without owning selection, network, or event state.
 * The controller keeps event delegation so rerendering never multiplies listeners.
 */
export function renderImageManagerGroupedListings({
  groupedData,
  selectedIds,
  expandedListings,
  formatNumber,
  onRenameProject,
  resolveGroupCounts,
}) {
  const container = document.getElementById('im-listings-container');
  if (!container) {
    console.warn('[ImageManager] Container not found!');
    return;
  }

  container.querySelectorAll('.im-listing-card').forEach((element) => element.remove());
  const loading = document.getElementById('im-images-loading');
  const empty = document.getElementById('im-images-empty');
  if (loading) loading.hidden = true;
  if (empty && groupedData.length > 0) empty.hidden = true;

  groupedData.forEach((group, listingIdx) => {
    const key = getImageManagerGroupKey(group);
    const isExpanded = expandedListings.has(key);
    const isStandalone = group.is_standalone || group.workflow === 'image_only' || !group.listing_id;
    let listingName = group.listing_name
      || (group.listing_id ? `Listing #${group.listing_id}` : '未关联 Listing');
    if (isStandalone && group.listing_name) {
      listingName = group.listing_name;
    } else if (isStandalone && !group.listing_id) {
      listingName = group.job_id ? `未命名批次 · ${group.job_id.slice(0, 8)}` : '未关联项目';
    }
    const batchCount = group.job_count || group.job_ids?.length || 0;
    if (batchCount > 1) listingName += ` · ${batchCount} 次生图`;
    const { completed: imageCount, inflight: inflightCount } = resolveGroupCounts(group);
    const countLabel = inflightCount > 0
      ? `${imageCount} 张 (+${inflightCount} 生成中)`
      : `${imageCount} 张`;
    const lastDate = group.last_image_at
      ? new Date(group.last_image_at).toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai' })
      : '';
    const images = group.images || [];
    const groupSelected = images.filter((img) => selectedIds.has(img.id)).length;
    const allSelected = images.length > 0 && groupSelected === images.length;
    const card = createElement('div', { className: 'im-listing-card' });
    card.append(createListingHeader(group, listingIdx, { listingName, isExpanded, lastDate, countLabel }));
    if (isExpanded) {
      card.append(createListingBody(images, listingIdx, {
        allSelected,
        groupSelected,
        selectedIds,
        formatNumber,
      }));
    }
    container.appendChild(card);
  });

  container.querySelectorAll('.im-rename-btn').forEach((button) => {
    button.addEventListener('click', (event) => {
      event.stopPropagation();
      onRenameProject(button);
    });
  });
  container.querySelectorAll('.im-continue-assets').forEach((link) => {
    link.addEventListener('click', (event) => event.stopPropagation());
  });
}
